const BASE = '/feeds-v2-monitor'
let sessionExpiresAt = 0
let sessionRequest

export async function ensureFeedsV2Session(force = false) {
  if (!force && sessionExpiresAt > Date.now() + 60000) return
  if (sessionRequest) return sessionRequest
  sessionRequest = (async () => {
    const token = localStorage.getItem('auth_token')
    if (!token) throw new Error('Sign in to the console to view Feeds V2.')
    const response = await fetch(`${BASE}/api/session`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}` },
      credentials: 'same-origin', signal: AbortSignal.timeout(10000),
    })
    if (!response.ok) throw new Error(response.status === 403
      ? 'Your account does not have permission to view Feeds V2.'
      : response.status === 401 ? 'Your console session has expired. Sign in again.'
      : 'The Feeds V2 connection is unavailable. Try reconnecting.')
    const data = await response.json()
    sessionExpiresAt = data.expiresAt || Date.now() + 60000
  })().finally(() => { sessionRequest = null })
  return sessionRequest
}

function scopeQuery({ fixtureId, playerId, market }) {
  return new URLSearchParams(Object.entries({ fixture_id: fixtureId, player_id: playerId, market }).filter(([, value]) => value != null).map(([key, value]) => [key, String(value)])).toString()
}

export async function fetchFeedsV2Market(scope, signal) {
  await ensureFeedsV2Session()
  const url = `${BASE}/api/snapshot?${scopeQuery(scope)}`
  const options = { credentials: 'same-origin', signal: AbortSignal.any([signal, AbortSignal.timeout(10000)]) }
  let response = await fetch(url, options)
  if (response.status === 401) {
    await ensureFeedsV2Session(true)
    response = await fetch(url, options)
  }
  if (!response.ok) throw new Error('Could not load the Feeds V2 engine state.')
  return response.json()
}

export function streamFeedsV2Market(scope) {
  return new EventSource(`${BASE}/events?${scopeQuery(scope)}`, { withCredentials: true })
}

export async function setFeedsV2Pricing(scope, enabled, signal) {
  return savePricing('/api/pricing', { ...scope, enabled }, signal)
}

export async function setFeedsV2FixturePricing(fixtureId, enabled, signal) {
  return savePricing('/api/fixture-pricing', { fixtureId, enabled }, signal)
}

export async function setFeedsV2SourceSelection(scope, bookId, signal) {
  return savePricing('/api/source-selection', { ...scope, bookId }, signal)
}

export async function setFeedsV2MarketFeedMode(scope, enabled, signal) {
  return savePricing('/api/market-feed-mode', { ...scope, enabled }, signal)
}

export async function fetchFeedsV2EventCandidates(fixtureId, signal) {
  await ensureFeedsV2Session()
  const url = `${BASE}/api/event-candidates?${scopeQuery({ fixtureId })}`
  const options = { credentials: 'same-origin', signal: AbortSignal.any([signal, AbortSignal.timeout(10000)]) }
  let response = await fetch(url, options)
  if (response.status === 401) {
    await ensureFeedsV2Session(true)
    response = await fetch(url, options)
  }
  if (!response.ok) throw new Error('Could not load OddsMarket games for this fixture. Try again.')
  return response.json()
}

export async function setFeedsV2EventMapping(fixtureId, eventIds, signal) {
  return savePricing('/api/event-mapping', { fixtureId, eventIds }, signal)
}

export async function activateEligibleFeedsV2(fixtureId, signal) {
  return savePricing('/api/activate-eligible', { fixtureId }, signal)
}

export async function deactivateEligibleFeedsV2(fixtureId, signal) {
  return savePricing('/api/deactivate-eligible', { fixtureId }, signal)
}

async function savePricing(path, body, signal) {
  await ensureFeedsV2Session()
  const options = {
    method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body), signal: AbortSignal.any([signal, AbortSignal.timeout(10000)]),
  }
  let response = await fetch(`${BASE}${path}`, options)
  if (response.status === 401) {
    await ensureFeedsV2Session(true)
    response = await fetch(`${BASE}${path}`, options)
  }
  const data = await response.json()
  if (!response.ok) {
    const messages = {
      pricing_fixture_not_allowed: 'Test pricing is disabled for this fixture.',
      pricing_fixture_not_found: 'This fixture no longer exists.',
      pricing_fixture_lookup_failed: 'Could not verify this fixture. No setting was changed; try again.',
      pricing_fixture_mode_off: 'Turn Feeds V2 ON in the fixture toolbar.',
      automatic_feed_mode: 'Feed assignment is automatic. Use the existing player and market switches to open or suspend markets.',
      pricing_permission_required: 'Your account does not have permission to change feed pricing.',
      pricing_engine_unavailable: 'The engine is unavailable. Reconnect before enabling pricing.',
      pricing_market_unavailable: 'This market has no current V2 coverage.',
      invalid_source_selection: 'Select consensus or an approved bookmaker.',
      market_must_be_suspended: 'Suspend this market before switching its feed OFF.',
      market_suspension_lookup_failed: 'Could not verify market suspension. The feed setting was not changed; try again.',
      pricing_players_unavailable: 'Could not verify player suspensions. No markets were activated; try again.',
      event_mapping_required: 'Confirm the OddsMarket game for this fixture before switching Feeds V2 ON.',
      event_not_bound_to_fixture: 'That OddsMarket game is no longer matched to this fixture. Reload the list.',
      event_candidates_unavailable: 'OddsMarket games are unavailable right now. No setting was changed; try again.',
      invalid_event_mapping: 'Select at least one OddsMarket game.',
    }
    throw new Error(messages[data.error] || 'Could not save feed pricing. Reconnect to check its current state.')
  }
  return data
}
